import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ship, Users, Calendar, Shield, ArrowRight, Anchor } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Landing() {
  const navigate = useNavigate();
  const [hoveredFeature, setHoveredFeature] = useState(null);

  const features = [
    {
      icon: Ship,
      title: 'Premium Fleet',
      description: 'Access 70+ luxury Nile cruise vessels with real-time availability.',
    },
    {
      icon: Calendar,
      title: 'Flexible Schedules',
      description: 'Multiple routes and durations with weekly departures from Luxor and Aswan.',
    },
    {
      icon: Users,
      title: 'B2B Platform',
      description: 'Exclusive platform for travel agents with competitive commissions.',
    },
    {
      icon: Shield,
      title: 'Secure Booking',
      description: 'Instant inventory hold with admin-confirmed reservations.',
    },
  ];

  return (
    <div className="landing-hero">
      <div className="landing-content min-h-screen flex flex-col">
        {/* Header */}
        <header className="py-6 px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4AF37] rounded-sm flex items-center justify-center">
              <Anchor className="w-6 h-6 text-white" />
            </div>
            <span className="logo-text text-2xl text-white">
              Nile Cruise <span className="logo-accent">Plus</span>
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <Button
              variant="ghost"
              className="text-white/80 hover:text-white hover:bg-white/10"
              onClick={() => navigate('/login')}
              data-testid="nav-agent-login"
            >
              Agent Login
            </Button>
            <Button
              variant="ghost"
              className="text-white/80 hover:text-white hover:bg-white/10"
              onClick={() => navigate('/admin/login')}
              data-testid="nav-admin-login"
            >
              Admin Portal
            </Button>
          </nav>
        </header>

        {/* Hero Content */}
        <main className="flex-1 flex items-center justify-center px-8 py-16">
          <div className="max-w-5xl w-full">
            <div className="text-center mb-16 animate-fade-in">
              <p className="text-[#D4AF37] font-medium tracking-wider uppercase text-sm mb-4">
                B2B Cruise Booking Platform
              </p>
              <h1 className="font-heading text-5xl md:text-7xl font-bold text-white tracking-tight leading-none mb-6">
                Navigate the Nile<br />
                <span className="text-[#D4AF37]">with Confidence</span>
              </h1>
              <p className="text-white/70 text-lg max-w-2xl mx-auto leading-relaxed">
                The premier booking platform for travel agents. Access premium Nile cruises, 
                manage inventory in real-time, and deliver unforgettable Egyptian experiences.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-20 animate-fade-in stagger-1">
              <Button
                size="lg"
                className="btn-gold text-sm px-8 py-6 rounded-sm"
                onClick={() => navigate('/register')}
                data-testid="cta-get-started"
              >
                Get Started as Agent
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-white/30 text-white hover:bg-white/10 px-8 py-6 rounded-sm"
                onClick={() => navigate('/login')}
                data-testid="cta-sign-in"
              >
                Sign In to Your Account
              </Button>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 animate-fade-in stagger-2">
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className={`p-6 rounded-sm border transition-all duration-300 cursor-default ${
                    hoveredFeature === index
                      ? 'bg-white/15 border-[#D4AF37]/50'
                      : 'bg-white/5 border-white/10'
                  }`}
                  onMouseEnter={() => setHoveredFeature(index)}
                  onMouseLeave={() => setHoveredFeature(null)}
                  data-testid={`feature-card-${index}`}
                >
                  <feature.icon className={`w-8 h-8 mb-4 ${hoveredFeature === index ? 'text-[#D4AF37]' : 'text-white/60'} transition-colors`} />
                  <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-6 px-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">
              © 2024 Nile Cruise Plus. All rights reserved.
            </p>
            <div className="flex gap-6">
              <span className="text-white/40 text-sm">Support: support@nilecruiseplus.com</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
